import { BellRing } from "lucide-react";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { useWebPush } from "../../../lib/useWebPush";

/**
 * Turning browser notifications on for THIS device.
 *
 * The matrix above says which categories may reach you on the push channel. It
 * cannot make push arrive: a browser only delivers to a device that has granted
 * permission and registered a subscription, and that is per device rather than
 * per account. Without this card the push column is a switch for a channel the
 * user has no way to open.
 *
 * The subscribe flow itself already existed and had exactly one caller - the
 * WordPress import progress page - which is a strange place for the only push
 * opt-in in the product.
 */
export function PushDevicesCard() {
  const t = useT();
  const push = useWebPush({ swPath: "/push-sw.js" });

  // A browser without a service worker, or a platform with no VAPID keys, has
  // nothing to offer. Drawing a switch that cannot work is worse than drawing
  // nothing.
  if (!push.supported || !push.vapidAvailable) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <BellRing className="size-4 text-primary" /> {t("pushDevices.title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">{t("pushDevices.intro")}</p>
        <div className="flex items-center gap-3">
          {push.subscribed ? (
            <>
              <span className="text-sm font-medium text-foreground">{t("pushDevices.on")}</span>
              <Button variant="outline" size="sm" loading={push.busy} onClick={() => void push.unsubscribe()}>
                {t("pushDevices.turnOff")}
              </Button>
            </>
          ) : (
            <>
              <span className="text-sm text-muted-foreground">{t("pushDevices.off")}</span>
              <Button size="sm" loading={push.busy} onClick={() => void push.subscribe()}>
                {t("pushDevices.turnOn")}
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
